import React, { useContext, useEffect, useRef, useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import { useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import MUTATION_UPDATE_CART_ITEM from '../../../../../../aem-core-components/queries/mutation_update_cart_item.graphql';
import QUERY_CART_DETAILS from '../../../../../../aem-core-components/queries/query_cart_details.graphql';
import { useCartState } from '../../../../../../contexts/cart';
import { useFilterState } from '../../../../../cap';
import { usePdpState } from '../../../../../../contexts/pdp/pdpContext';
import { useAwaitQuery } from '../../../../../../aem-core-components/utils/hooks';
import { useAtp } from '../../../../../cap/hooks/useATP';
import useCartOptions from '../../../../../../hooks/useCartOptions';
import useAnalytics from '../../../../../../hooks/analytics/useAnalytics';
import { useAnalyticsContext } from '../../../../../../config/GoogleTagManagerEvents';
import { ModalContext } from '../../../../../../aem-core-components/context/ModalContext';
import RentalReviewListView from '../../../../../checkoutv2/RentalReviewList/RentalReviewListView/RentalReviewListView';
import Button from '../../../../atoms/button';
import MaxQtyErrorComponent from '../../../../atoms/maxQtyErrorComponent';
import ForcedItemsDetails from '../forcedItemsDetails/ForcedItemsDetails';
import { VARIABLE_CONFIG } from '../../../../../../constants/analyticsConstants/Variables';
import { logError } from '../../../../utils/logger';
import classes from './addedToCart.css';
import { pdpDataLocators } from '../../../../../pdp/dataLocators';
import { EVENT_PAGE_NAMES_CONFIG } from '../../../../../../constants/analyticsConstants/Page';
import config from '../../../../../App/config';
import { QTY_LIMIT, THROTTLE_DELAYS } from '../../../../../pdp/constants';
import { STORAGE_CONFIG } from '../../../../../../constants/storageConfig';
import { SET_IS_ITEM_PC_ADDED } from '../../../../../../aem-core-components/actions/constants';
import { PDP_SET_ADDED_QTY } from '../../../../../../contexts/pdp/constants';

const AddedToCart = props => {
    const [{ cart, isItemPCAdding  }, cartDispatch] = useCartState();
    const [{ startDate, endDate }] = useFilterState();
    const [, pdpDispatch] = usePdpState();
    const {
        handleCloseModal = () => {},
        localItemObj = {
            title: '-',
            img: '',
            qty: 1,
            price: '-'
        },
        forcedItemDetails = {},
        counterCustomClass = '',
        isAddedFromPdp = false,
        productId = ''
    } = props;
    const intl = useIntl();
    const body = document.querySelector('body');
    const useModalState = () => useContext(ModalContext);
    const { closeModal } = useModalState();
    const { sendEventsForEcommercePage, sendEventsForPageUser } = useAnalyticsContext();
    const [{ payloadEcommerceLocationActionAnalytics }] = useAnalytics();
    const [maxQtyErrorMsg, setMaxQtyErrorMsg] = useState('');
    const [isMaxQuantityReached, setIsMaxQuantityReached] = useState(false);
    const [addedToCartQuantity, setAddedToCartQuantity] = useState(localItemObj?.qty || 1);
    const qtyUpdateTimeout = useRef(null);
    const latestQty = useRef(null);
    const orderEstimates = cart?.estimatesResponse?.estimate;
    const [updateCartItemMutation] = useMutation(MUTATION_UPDATE_CART_ITEM);
    const cartDetailsQuery = useAwaitQuery(QUERY_CART_DETAILS);
    const { handleATPCart } = useAtp();
    const [, { updateLocalCart }] = useCartOptions({
        updateCartItemMutation,
        cartDetailsQuery,
        handleATPCart
    });

    useEffect(() => {
        if (isMaxQuantityReached) {
            setMaxQtyErrorMsg(
                `${intl.formatMessage({
                    id: 'pdp:counter-max-quantity-limit-msg-prefix'
                })} (${QTY_LIMIT}) ${intl.formatMessage({
                    id: 'pdp:counter-max-quantity-limit-msg-suffix'
                })}`
            );
        } else {
            setMaxQtyErrorMsg('');
        }
    }, [isMaxQuantityReached]);

    const handleQtyCounterUpdate = async newVal => {
        const localAssetsData = JSON.parse(
            sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.LOCAL_ASSETS_DATA) || '[]'
        );
        const existingProduct = localAssetsData?.find(item => item?.productId === productId) || {};
        cartDispatch({ type: SET_IS_ITEM_PC_ADDED, isItemPCAdding: true });
        await updateLocalCart([{ cart_item_uid: existingProduct?.uid, quantity: newVal }], productId);
        cartDispatch({ type: SET_IS_ITEM_PC_ADDED, isItemPCAdding: false });
    };

    const handleQtyChange = async (qty, delay) => {
        latestQty.current = qty;
        clearTimeout(qtyUpdateTimeout?.current);
        qtyUpdateTimeout.current = setTimeout(async () => {        
            if (latestQty?.current !== '' && latestQty?.current !== 0 && latestQty?.current <= QTY_LIMIT) {
                setAddedToCartQuantity(latestQty?.current);
                if (isAddedFromPdp) {
                    pdpDispatch({
                        type: PDP_SET_ADDED_QTY,
                        addedQty: latestQty?.current
                    });
                }
                await handleQtyCounterUpdate(latestQty?.current);
            }
        }, delay);
    };

    const handleInputChange = value => {
        handleQtyChange(value, THROTTLE_DELAYS.INPUT_BOX);
    };

    const handleIncDecButtonClick = value => {
        handleQtyChange(value, THROTTLE_DELAYS.INC_DEC_BUTTON);
    };

    const handleInputChangeOnBlur = (element, value) => {
        if (element?.value === '') {
            setAddedToCartQuantity(value);
            if (isAddedFromPdp) {
                pdpDispatch({
                    type: PDP_SET_ADDED_QTY,
                    addedQty: value
                });
            }
        }
    };

    const onKeepShoppingCartClick = () => {
        closeModal();
        handleCloseModal();
        body.classList.remove('overflow-hidden');
    };

    const handleViewCart = () => {
        closeModal();
        window.location.href = window.location.origin + config?.pagePaths?.cartPage;
    };

    const isRentalDatesEmpty = () => {
        return !(startDate && endDate);
    };

    const handleViewCartClick = () => {
        //track navigation to cart for all users - credit,cash and guest
        try {
            sendEventsForEcommercePage(
                VARIABLE_CONFIG.ECOMMERCE_PAGE.CART,
                localStorage.getItem('companyID') == 2
                    ? VARIABLE_CONFIG.CURRENCY_CODE.CANADA
                    : VARIABLE_CONFIG.CURRENCY_CODE.USA
            );
            sendEventsForPageUser(EVENT_PAGE_NAMES_CONFIG.PAGE_USER, payloadEcommerceLocationActionAnalytics());
        } catch (error) {
            logError(error, false, 'handleViewCartClick');
        }
        closeModal();
        handleViewCart();
    };

    const renderDetails = () => {
        return (
            <>
                <RentalReviewListView
                    isRentalDatesEmpty={isRentalDatesEmpty()}
                    orderEstimates={orderEstimates}
                    isFromPDP={true}
                    pdpQty={addedToCartQuantity}
                    localItemObj={localItemObj}
                    tabIndexValue={-1}
                    handleInputChange={handleInputChange}
                    handleIncDecButtonClick={handleIncDecButtonClick}
                    handleInputChangeOnBlur={handleInputChangeOnBlur}
                    counterCustomClass={counterCustomClass}
                    setIsMaxQuantityReached={setIsMaxQuantityReached}
                    readOnly={isItemPCAdding}
                />
                {
                    (forcedItemDetails?.addons?.length > 0 || forcedItemDetails?.rentals?.length > 0 )&& 
                    <ForcedItemsDetails
                        forcedItemDetails={forcedItemDetails}
                        mainProductQty={addedToCartQuantity}
                    />
                }
                
            </>
        );
    };

    return (
        <div className={classes.addedToCartRoot}>
            <div className={classes.listStyle}>{renderDetails() || ''}</div>
            <MaxQtyErrorComponent
                qtyError={maxQtyErrorMsg}
                dataTestId={pdpDataLocators.pdp_add_to_cart_quantity_exceeded_error_label}
            />
            <div className={classes.ratesAvailabilityInfo}>
                {intl.formatMessage({ id: 'pdp:added-to-cart-rates-availability-info' })}
            </div>
            <div className={classes.buttonList}>
                <div className={classes.viewCartButton}>
                    <Button
                        className="button button-primary button-block button-marginbottom"
                        type="button"
                        onClick={handleViewCartClick}
                        data-testid={pdpDataLocators.added_to_cart_view_your_cart_cta}
                        buttonAriaLabel={intl.formatMessage({ id: 'pdp:alert-btn-viewcart' })}>
                        {intl.formatMessage({ id: 'pdp:alert-btn-viewcart' })}
                    </Button>
                </div>
                <Button
                    type="button"
                    className={'button button-outline-primary'}
                    onClick={onKeepShoppingCartClick}
                    data-testid={pdpDataLocators.added_to_cart_keep_shopping_cta}
                    buttonAriaLabel={intl.formatMessage({ id: 'pdp:alert-btn-keep-shopping' })}>
                    {intl.formatMessage({ id: 'pdp:alert-btn-keep-shopping' })}
                </Button>
            </div>
        </div>
    );
};
AddedToCart.propTypes = {
    handleCloseModal: func,
    localItemObj: object,
    forcedItemDetails: object,
    counterCustomClass: string,
    isAddedFromPdp: bool,
    productId: string
};

export default AddedToCart;
